import {
  createRef,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Collapse } from "@mui/material";
import {
  CloseOutlined,
  DeleteOutlined,
  PlusOutlined,
  SearchOutlined,
  SmileOutlined,
} from "@ant-design/icons";
import { SEARCH_ITEM_LIST_HEIGHT, ItemCategoryWithBought } from "../../lib/lib";
import {
  ItemWithPopularity,
  ShoppingListItemIdAndAmount,
} from "../../lib/models";
import { callEndpoint, doFormalize as doNormalize } from "../../lib/functions";
import { AddItemContent } from "../shopping/addItemContent";
import { DataContext } from "../../lib/contexts";
import { categoryToColor } from "../../lib/lib";
import { categoryToIcon } from "../../lib/definitions";
import { addItemToStats } from "../../lib/statisticsHandler";
import { Spinner } from "../general/spinner";
import { MInput } from "../elements/mInputs";
import { MIconButton } from "../elements/miconButton";
import { MButton } from "../elements/mbutton";
import { MModal } from "../elements/mmodal";
import { CreateItemContent } from "../shopping/createItemContent";
import { ToastContainer } from "react-toastify";
import {
  ItemCategory,
  UpdateShoppingListAction,
  UpdateShoppingListEndpoint,
} from "@ckal-software/ckal-lib/dist/apps/madplanen";
import { useNotifcation } from "../../lib/hooks/useNotification";
import { EcoLabel } from "../elements/ecoLabel";
import { SelectPlaceButton } from "../shopping/SelectPlaceButton";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { useScrollToTop } from "../../lib/hooks/useScrollToTop";

// ── Inline styles ──────────────────────────────────────────────

import { TEXT_COLOR } from "../../lib/style_definitions";
import css from "./shoppingListView.module.css";

const styles = {
  searchBackdrop: (open: boolean) => ({
    position: "fixed" as const,
    inset: 0,
    background: "rgba(0,0,0,0.25)",
    zIndex: 90,
    opacity: open ? 1 : 0,
    pointerEvents: (open
      ? "auto"
      : "none") as React.CSSProperties["pointerEvents"],
    transition: "opacity 0.3s ease",
  }),
  categoryIconWrap: (color: string) => ({
    width: 32,
    height: 32,
    borderRadius: 10,
    background: `color-mix(in srgb, ${color} 15%, transparent)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
  }),
  categoryLabel: (color: string) => ({
    fontWeight: 700 as const,
    fontSize: 13,
    letterSpacing: 0.6,
    color,
    textTransform: "uppercase" as const,
  }),
  checkbox: (checked: boolean, color: string) => ({
    width: 22,
    height: 22,
    borderRadius: 7,
    border: checked
      ? "none"
      : `2px solid color-mix(in srgb, ${color} 25%, transparent)`,
    background: checked ? `${color}` : "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "all 0.2s",
  }),
  itemName: (bought: boolean) => ({
    fontSize: 14,
    fontWeight: 500 as const,
    color: bought ? "#b0b0b0" : TEXT_COLOR,
    textDecoration: bought ? ("line-through" as const) : ("none" as const),
  }),
};

function SwipeableItem({
  children,
  onClick,
  onRemove,
}: {
  children: ReactNode;
  onClick(): void;
  onRemove(): void;
}) {
  const [offset, setOffset] = useState(0);
  const [isRemoving, setIsRemoving] = useState(false);
  const suppressClick = useRef(false);
  const gesture = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    direction: "pending" | "horizontal" | "vertical" | "removing";
  } | null>(null);

  useEffect(() => {
    if (!isRemoving) {
      return;
    }

    const timer = window.setTimeout(onRemove, 240);

    return () => window.clearTimeout(timer);
  }, [isRemoving, onRemove]);

  function resetSwipe() {
    if (gesture.current?.direction === "removing") {
      return;
    }

    gesture.current = null;
    setOffset(0);
  }

  return (
    <div className={css.swipeItem}>
      <div
        className={`${css.swipeAction} ${offset < 0 ? css.swipeActionRight : ""}`}
        style={{ opacity: Math.min(Math.abs(offset) / 72, 1) }}
        aria-hidden="true"
      >
        <DeleteOutlined />
      </div>
      <div
        className={`shopping-item-row ${css.itemRow} ${isRemoving ? css.itemRowRemoving : offset !== 0 ? css.itemRowSwiping : ""}`}
        style={{ transform: `translateX(${offset}px)` }}
        onPointerDown={(event) => {
          if (
            !event.isPrimary ||
            event.button !== 0 ||
            gesture.current?.direction === "removing"
          ) {
            return;
          }

          suppressClick.current = false;
          gesture.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            direction: "pending",
          };
        }}
        onPointerMove={(event) => {
          const currentGesture = gesture.current;

          if (!currentGesture || currentGesture.pointerId !== event.pointerId) {
            return;
          }

          const deltaX = event.clientX - currentGesture.startX;
          const deltaY = event.clientY - currentGesture.startY;

          if (currentGesture.direction === "pending") {
            if (
              Math.abs(deltaX) > 10 &&
              Math.abs(deltaX) > Math.abs(deltaY) * 1.2
            ) {
              currentGesture.direction = "horizontal";
              suppressClick.current = true;
              event.currentTarget.setPointerCapture(event.pointerId);
            } else if (Math.abs(deltaY) > 10) {
              currentGesture.direction = "vertical";
              suppressClick.current = true;
            }
          }

          if (currentGesture.direction === "horizontal") {
            event.preventDefault();
            const width = event.currentTarget.clientWidth;
            setOffset(Math.max(-width, Math.min(deltaX, width)));
          }
        }}
        onPointerUp={(event) => {
          const currentGesture = gesture.current;

          if (!currentGesture || currentGesture.pointerId !== event.pointerId) {
            return;
          }

          const distance = Math.abs(event.clientX - currentGesture.startX);
          const threshold = event.currentTarget.clientWidth / 4;
          const shouldRemove =
            currentGesture.direction === "horizontal" && distance >= threshold;

          if (shouldRemove) {
            currentGesture.direction = "removing";
            setIsRemoving(true);
            setOffset(
              Math.sign(event.clientX - currentGesture.startX) *
                event.currentTarget.clientWidth,
            );
          } else {
            resetSwipe();
          }
        }}
        onPointerCancel={resetSwipe}
        onLostPointerCapture={(event) => {
          if (event.target === event.currentTarget) {
            resetSwipe();
          }
        }}
        onClickCapture={(event) => {
          if (suppressClick.current) {
            event.preventDefault();
            event.stopPropagation();
            suppressClick.current = false;
          }
        }}
        onClick={onClick}
      >
        {children}
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────

export function ShoppingListView() {
  const [itemName, setItemName] = useState("");
  const searchResultsRef = useScrollToTop(itemName);
  const [showCreateItemModal, setShowCreateItemModal] = useState(false);
  const [showAddItemDropdown, setShowAddItemDropdown] = useState(false);
  const [amount, setAmount] = useState(1);
  const [orderedCategories, setOrderedCategories] = useState<
    ItemCategoryWithBought[]
  >([]);

  const notify = useNotifcation();
  const latestUpdate = useRef(Date.now());

  const {
    shoppingList,
    setShoppingList,
    items,
    itemsMap,
    isFetchingItems,
    selectedCategoryOrderId,
    categoryOrders,
  } = useContext(DataContext);

  const selectedCategoryOrder = useMemo(
    () => categoryOrders.find((co) => co._id === selectedCategoryOrderId),
    [selectedCategoryOrderId, categoryOrders],
  );

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let categories =
      selectedCategoryOrder?.order ?? Object.values(ItemCategory);
    categories = categories.concat(
      ...(Object.values(ItemCategory).filter(
        (k) => !categories.includes(k as ItemCategory),
      ) as ItemCategory[]),
    );
    setOrderedCategories(categories);
  }, [selectedCategoryOrder]);

  // ── Business logic (unchanged) ───────────────────────────────

  function bulkMoveItems(
    fromCategory: ItemCategoryWithBought,
    action: UpdateShoppingListAction,
  ) {
    if (fromCategory === "Købte") {
      updateShoppingList(shoppingList?.boughtItems || [], action);
    } else {
      updateShoppingList(
        (shoppingList?.items || []).filter((it) =>
          fromCategory === ItemCategory.Other
            ? !!it.isOther
            : !it.isOther && itemsMap[it._id].category === fromCategory,
        ),
        action,
      );
    }
  }

  async function updateShoppingList(
    items: ShoppingListItemIdAndAmount[],
    action: UpdateShoppingListAction,
    isOther?: number,
  ) {
    if (!shoppingList) {
      notify("An error occurred");
      return;
    }

    items = isOther ? items.map((i) => ({ ...i, isOther })) : items;

    let newItems = shoppingList.items;
    let newBoughtItems = shoppingList.boughtItems;

    if (action === UpdateShoppingListAction.Add) {
      items.forEach((item) => {
        const existingItem = newItems.find((i) => i._id === item._id);
        if (existingItem) {
          existingItem.amount += item.amount;
        } else {
          newItems.unshift(item);
        }
      });
      newBoughtItems = newBoughtItems.filter(
        (item) => !items.find((itemToMove) => itemToMove._id === item._id),
      );
    } else if (action === UpdateShoppingListAction.Buy) {
      newItems = newItems.filter(
        (item) => !items.find((itemToMove) => itemToMove._id === item._id),
      );
      newBoughtItems.unshift(...items);
    } else if (action === UpdateShoppingListAction.Remove) {
      newItems = newItems.filter(
        (item) => !items.find((itemToRemove) => itemToRemove._id === item._id),
      );
      newBoughtItems = newBoughtItems.filter(
        (item) => !items.find((itemToRemove) => itemToRemove._id === item._id),
      );
    }

    newItems = Array.from(new Set(newItems.map((i) => i._id))).map(
      (id) => newItems.find((i) => i._id === id)!,
    );
    newBoughtItems = Array.from(new Set(newBoughtItems.map((i) => i._id))).map(
      (id) => newBoughtItems.find((i) => i._id === id)!,
    );

    setShoppingList({
      ...shoppingList,
      items: newItems,
      boughtItems: newBoughtItems,
    });
    const updatedAt = Date.now();
    latestUpdate.current = updatedAt;
    const [err, data] = await callEndpoint(UpdateShoppingListEndpoint, {
      items,
      action,
    });

    if (err) {
      notify("Opdatering af indkøbsliste: Der skete en fejl");
      return;
    }

    if (latestUpdate.current === updatedAt) {
      setShoppingList(data);
    }
  }

  const filteredItems: ItemWithPopularity[] = useMemo(
    () =>
      items.filter((item) =>
        doNormalize((item.isOrganic ? "økologisk " : "") + item.name).includes(
          doNormalize(itemName),
        ),
      ),
    [itemName, items],
  );

  function confirmItemName() {
    if (!itemName) return;

    const existingItem = filteredItems.find(
      (item) => doNormalize(item.name) === doNormalize(itemName),
    );
    if (existingItem) {
      addItem(existingItem._id);
    } else if (
      shoppingList?.items.find(
        (item) =>
          doNormalize(itemsMap[item._id]!.name) === doNormalize(itemName),
      )
    ) {
      notify(`${itemName} er allerede på din indkøbsliste`);
    } else {
      setShowCreateItemModal(true);
    }
  }

  function addItem(itemId: string, isOther?: number) {
    updateShoppingList(
      [{ _id: itemId, amount: amount }],
      UpdateShoppingListAction.Add,
      isOther,
    );
    if (!isOther) {
      addItemToStats(itemId);
    }
    setItemName("");
    setAmount(1);
  }

  const itemIsOnList = useMemo(
    () =>
      !!shoppingList?.items.find(
        (item) =>
          doNormalize(
            item.isOther ? item._id : itemsMap[item._id]?.name || "",
          ) === doNormalize(itemName),
      ),
    [shoppingList, itemName, itemsMap],
  );

  const itemsInCategory: { [k in string]: ShoppingListItemIdAndAmount[] } =
    useMemo(() => {
      if (!shoppingList) return {};

      const result = {} as Record<
        ItemCategoryWithBought,
        ShoppingListItemIdAndAmount[]
      >;
      Object.values(ItemCategory).forEach(
        (category) => (result[category] = []),
      );
      shoppingList.items.forEach((i) =>
        result[
          i.isOther
            ? ItemCategory.Other
            : itemsMap[i._id]?.category || ItemCategory.Other
        ].push(i),
      );
      return result;
    }, [shoppingList, itemsMap]);

  useEffect(() => {
    if (showAddItemDropdown) inputRef.current?.focus();
  }, [showAddItemDropdown]);

  const sortedItemOptions = useMemo(() => {
    const normalizedQuery = doNormalize(itemName);

    return filteredItems
      .sort((leftItem, rightItem) => {
        const leftName = doNormalize(leftItem.name);
        const rightName = doNormalize(rightItem.name);
        const leftRank =
          leftName === normalizedQuery
            ? 0
            : leftName.startsWith(normalizedQuery)
              ? 1
              : 2;
        const rightRank =
          rightName === normalizedQuery
            ? 0
            : rightName.startsWith(normalizedQuery)
              ? 1
              : 2;

        if (leftRank !== rightRank) {
          return leftRank - rightRank;
        }

        if (leftItem.popularity === rightItem.popularity) {
          return leftItem.name.localeCompare(rightItem.name);
        }

        return (rightItem.popularity || 0) - (leftItem.popularity || 0);
      })
      .slice(0, 30);
  }, [filteredItems, itemName]);

  const suggestions = useMemo(() => {
    const itemsOnList = shoppingList?.items.map((i) => i._id) || [];
    return [...items]
      .filter((i) => !itemsOnList.includes(i._id))
      .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
      .slice(0, 40);
  }, [items, shoppingList]);

  const suggestionRefs = useMemo(
    () =>
      Object.fromEntries(
        suggestions.map((s) => [s._id, createRef<HTMLDivElement>()]),
      ),
    [suggestions],
  );

  const categoryRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>(
    {},
  );
  const getCategoryRef = (key: string) => {
    if (!categoryRefs.current[key])
      categoryRefs.current[key] = createRef<HTMLDivElement>();
    return categoryRefs.current[key];
  };

  const totalItemCount =
    (shoppingList?.items.length ?? 0) + (shoppingList?.boughtItems.length ?? 0);

  // ── Render helpers ───────────────────────────────────────────

  function renderItem(
    item: ShoppingListItemIdAndAmount,
    category: ItemCategoryWithBought,
    onItemClick: () => void,
    onAmountClick: () => void,
    onAmountLongPress: () => void,
  ) {
    const isBought = category === "Købte";
    const color = categoryToColor[category];
    const name = item.isOther ? item._id : itemsMap[item._id]?.name || "";
    const isOrganic =
      item.isOther === 2 || (!item.isOther && itemsMap[item._id]?.isOrganic);

    return (
      <SwipeableItem
        key={item._id}
        onClick={onItemClick}
        onRemove={() =>
          updateShoppingList([item], UpdateShoppingListAction.Remove)
        }
      >
        <div className={css.itemRowLeft}>
          <div style={styles.checkbox(isBought, color)}>
            {isBought && <span className={css.checkmark}>✓</span>}
          </div>
          <span style={styles.itemName(isBought)}>{name}</span>
          {isOrganic && (
            <EcoLabel
              size={1}
              color={isBought ? "#d1d5db" : undefined}
              style={{ flexShrink: 0 }}
            />
          )}
        </div>
        {!isBought && (
          <div
            className={`${css.amountBadge} ${item.amount !== 1 ? css.amountBadgeHighlighted : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onAmountClick();
            }}
            onPointerDown={(e) => {
              const timer = setTimeout(() => {
                cleanup();
                e.stopPropagation();
                onAmountLongPress();
              }, 400);
              const cleanup = () => {
                clearTimeout(timer);
                window.removeEventListener("pointermove", cancelOnMove);
                window.removeEventListener("pointerup", cleanup);
                window.removeEventListener("pointercancel", cleanup);
              };
              const cancelOnMove = (event: PointerEvent) => {
                if (
                  Math.abs(event.clientX - e.clientX) > 10 ||
                  Math.abs(event.clientY - e.clientY) > 10
                ) {
                  cleanup();
                }
              };

              window.addEventListener("pointermove", cancelOnMove);
              window.addEventListener("pointerup", cleanup, { once: true });
              window.addEventListener("pointercancel", cleanup, { once: true });
            }}
          >
            {item.amount}
          </div>
        )}
      </SwipeableItem>
    );
  }

  function renderCategoryCard(
    category: ItemCategoryWithBought,
    categoryItems: ShoppingListItemIdAndAmount[],
    onItemClick: (item: ShoppingListItemIdAndAmount) => void,
    onAmountClick: (item: ShoppingListItemIdAndAmount, amount?: number) => void,
    onClear: () => void,
  ) {
    const color = categoryToColor[category];
    const isBought = category === "Købte";

    return (
      <div key={category} className={css.categoryCard}>
        {/* header */}
        <div className={css.categoryHeader}>
          <div className={css.categoryHeaderLeft}>
            <div style={styles.categoryIconWrap(color)}>
              {categoryToIcon[category]}
            </div>
            <span style={styles.categoryLabel(color)}>{category}</span>
            <span className={css.categoryCount}>{categoryItems.length}</span>
          </div>
          {isBought && (
            <MButton
              type="ghost"
              style={{ height: 28, padding: "0 6px", fontSize: 14 }}
              onClick={onClear}
            >
              Ryd
            </MButton>
          )}
        </div>

        {/* items */}
        {categoryItems.map((item) =>
          renderItem(
            item,
            category,
            () => onItemClick(item),
            () => onAmountClick(item),
            () => onAmountClick(item, 1 - item.amount),
          ),
        )}
      </div>
    );
  }

  // ── JSX ──────────────────────────────────────────────────────

  return (
    <div className={css.wrapper}>
      <ToastContainer style={{ marginBottom: 90 }} />

      <>
        {/* ── Backdrop ──────────────────────────────── */}
        <div
          style={styles.searchBackdrop(showAddItemDropdown)}
          onClick={() => setShowAddItemDropdown(false)}
        />

        {/* ── Search box (bar + results) ────────────── */}
        <div className={css.searchHeaderArea}>
          <div
            className={`${css.searchHeader} ${showAddItemDropdown ? css.searchHeaderOpen : ""}`}
          >
            <div className={css.searchInputRow}>
              <MInput
                prefix={<SearchOutlined className={css.searchIcon} />}
                size="large"
                placeholder="Søg varer..."
                innerref={inputRef}
                onFocus={() => setShowAddItemDropdown(true)}
                allowClear
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                onPressEnter={confirmItemName}
                className="new-item-input"
              />
              {!showAddItemDropdown && categoryOrders.length > 1 && (
                <SelectPlaceButton style={{ marginLeft: 0 }} />
              )}
              {showAddItemDropdown && (
                <MIconButton
                  title="Tilføj vare"
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!itemName.trim()}
                  onClick={confirmItemName}
                />
              )}
              {showAddItemDropdown && (
                <MIconButton
                  icon={<CloseOutlined />}
                  onClick={() => setShowAddItemDropdown(false)}
                />
              )}
            </div>

            <Collapse
              in={showAddItemDropdown}
              timeout={350}
              mountOnEnter
              unmountOnExit
              className={css.searchResults}
            >
              <div
                className={css.searchResultsList}
                style={{ maxHeight: SEARCH_ITEM_LIST_HEIGHT }}
                ref={searchResultsRef}
              >
                {isFetchingItems ? (
                  <Spinner />
                ) : (
                  <AddItemContent
                    itemsOnList={shoppingList?.items || []}
                    sortedItemOptions={sortedItemOptions}
                    currentItemName={itemName}
                    itemIsOnList={itemIsOnList}
                    onNewItem={() => setShowCreateItemModal(true)}
                    addItemToList={(item) => {
                      inputRef.current?.focus();
                      addItem(item._id);
                    }}
                  />
                )}
              </div>
            </Collapse>
          </div>
        </div>

        {/* ── Scrollable content ────────────────────── */}
        <div className={css.listContainer}>
          {/* ── Suggestion chips ──────────────────────── */}
          <div
            className={`${css.suggestionsWrapper} ${totalItemCount === 0 ? css.suggestionsExpanded : ""}`}
          >
            <div className={css.suggestionsRow}>
              <TransitionGroup component={null}>
                {suggestions.map((i) => (
                  <CSSTransition
                    key={i._id}
                    timeout={200}
                    classNames="suggestion-chip"
                    nodeRef={
                      suggestionRefs[i._id] as React.RefObject<
                        HTMLElement | undefined
                      >
                    }
                  >
                    <div
                      ref={suggestionRefs[i._id]}
                      onClick={() => addItem(i._id)}
                      className={css.suggestionChip}
                    >
                      <span>{i.name}</span>
                      {i.isOrganic && (
                        <EcoLabel size={1} style={{ marginBottom: -1 }} />
                      )}
                    </div>
                  </CSSTransition>
                ))}
              </TransitionGroup>
            </div>
            {totalItemCount > 0 && <div className={css.suggestionsFade} />}
          </div>

          {/* ── Shopping list ─────────────────────────── */}
          {totalItemCount === 0 ? (
            <div className={css.emptyState}>
              <SmileOutlined className={css.emptyIcon} />
              <span className={css.emptyText}>Din indkøbsliste er tom</span>
            </div>
          ) : (
            <>
              <TransitionGroup component={null}>
                {orderedCategories
                  .filter((cat) => itemsInCategory[cat]?.length > 0)
                  .map((cat) => (
                    <CSSTransition
                      key={cat}
                      timeout={300}
                      classNames="category-card"
                      nodeRef={
                        getCategoryRef(cat) as React.RefObject<
                          HTMLElement | undefined
                        >
                      }
                    >
                      <div ref={getCategoryRef(cat)}>
                        {renderCategoryCard(
                          cat,
                          itemsInCategory[cat],
                          (item) =>
                            updateShoppingList(
                              [item],
                              UpdateShoppingListAction.Buy,
                            ),
                          (item, amt) =>
                            updateShoppingList(
                              [{ ...item, amount: amt ?? 1 }],
                              UpdateShoppingListAction.Add,
                            ),
                          () =>
                            bulkMoveItems(cat, UpdateShoppingListAction.Buy),
                        )}
                      </div>
                    </CSSTransition>
                  ))}

                {/* bought items */}
                {(shoppingList?.boughtItems.length ?? 0) > 0 && (
                  <CSSTransition
                    key="Købte"
                    timeout={300}
                    classNames="category-card"
                    nodeRef={
                      getCategoryRef("Købte") as React.RefObject<
                        HTMLElement | undefined
                      >
                    }
                  >
                    <div ref={getCategoryRef("Købte")}>
                      {renderCategoryCard(
                        "Købte",
                        shoppingList!.boughtItems,
                        (item) =>
                          updateShoppingList(
                            [item],
                            UpdateShoppingListAction.Add,
                            item.isOther,
                          ),
                        () => {},
                        () =>
                          bulkMoveItems(
                            "Købte",
                            UpdateShoppingListAction.Remove,
                          ),
                      )}
                    </div>
                  </CSSTransition>
                )}
              </TransitionGroup>

              <div style={{ height: 192 }} />
            </>
          )}
        </div>
      </>

      {/* ── Create item modal ──────────────────────── */}
      <MModal
        visible={showCreateItemModal}
        onClose={() => {
          setShowCreateItemModal(false);
          inputRef.current?.focus();
        }}
        title="Opret ny vare"
        hideCloseIcon
        content={
          showCreateItemModal ? (
            <CreateItemContent
              onClose={() => {
                setShowCreateItemModal(false);
                inputRef.current?.focus();
              }}
              onFinish={(item, isOther) => {
                inputRef.current?.focus();
                addItem(item, isOther);
                setShowCreateItemModal(false);
              }}
              initialItemName={itemName}
            />
          ) : null
        }
      />
    </div>
  );
}
